import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtIDR, fmtDate, RATE_META, STATUS_META } from "@/lib_app/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Upload, Download, Pencil, Trash2, Rows3, LayoutGrid, MoreHorizontal, MoreVertical, CheckSquare, PartyPopper, XCircle, RotateCcw, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import Pagination, { paginate } from "@/components/app/Pagination";
import ProspectFormModal, { LEAD_SOURCE_META } from "@/components/app/ProspectFormModal";
import ProspectDetailModal from "@/components/app/ProspectDetailModal";
import DealModal from "@/components/app/DealModal";
import AttachmentPopover from "@/components/app/AttachmentPopover";
import ImportProspectModal from "@/components/app/ImportProspectModal";
import Avatar from "@/components/app/Avatar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Prospects() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterRate, setFilterRate] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [filterLeadSource, setFilterLeadSource] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailProspect, setDetailProspect] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dealFor, setDealFor] = useState(null);
  const [filterStatus, setFilterStatus] = useState("active"); // active | won | lost | all
  const [compact, setCompact] = useState(() => localStorage.getItem("sp_compact") === "1");
  const [highlightedId, setHighlightedId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [filterBulan, setFilterBulan] = useState("all"); // "all" or "YYYY-MM"
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [scope, setScope] = useState(() => {
    // Owner never has personal prospek → always "all"; others: use localStorage or default "mine"
    return localStorage.getItem("sp_scope") || "mine";
  });
  const [sortBy, setSortBy] = useState(null); // column key
  const [sortDir, setSortDir] = useState(null); // "asc" | "desc" | null
  const [searchParams, setSearchParams] = useSearchParams();

  // Force owner into "all" scope (owner has no personal prospek)
  useEffect(() => { if (isOwner && scope !== "all") setScope("all"); }, [isOwner, scope]);

  // Bulk selection mode (mirrors Klien)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef(null);
  useEffect(() => {
    if (!kebabOpen) return;
    const onDoc = (e) => { if (kebabRef.current && !kebabRef.current.contains(e.target)) setKebabOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [kebabOpen]);
  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const toggleId = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const canDelete = user?.role === "owner" || user?.role === "sales_director";
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Hapus ${ids.length} prospek terpilih?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/prospects/bulk-delete", { prospect_ids: ids });
      const deleted = r.data?.deleted || 0;
      const skipped = r.data?.skipped || 0;
      if (deleted > 0) toast.success(`${deleted} prospek dihapus${skipped ? ` · ${skipped} dilewati` : ""}`);
      else toast.warning("Tidak ada prospek yang bisa dihapus");
      exitSelectionMode();
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setBulkBusy(false); }
  };
  const handleRowOpen = (r) => {
    if (selectionMode) { toggleId(r.id); return; }
    setDetailProspect(r); setDetailOpen(true);
  };

  useEffect(() => { localStorage.setItem("sp_compact", compact ? "1" : "0"); }, [compact]);
  useEffect(() => { localStorage.setItem("sp_scope", scope); }, [scope]);

  const load = async () => {
    try {
      const [p, u] = await Promise.all([api.get("/prospects"), api.get("/users")]);
      setRows(p.data);
      setUsers(u.data);
    } catch {
      toast.error("Gagal memuat data");
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-open modal when navigated with ?open={id}
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    // Refresh data first so we get the freshest prospect state (e.g. last_follow_up just updated
    // from NotificationQuickView), then open the modal with the fresh row.
    (async () => {
      try {
        const [p, u] = await Promise.all([api.get("/prospects"), api.get("/users")]);
        setRows(p.data);
        setUsers(u.data);
        const row = p.data.find((r) => r.id === openId);
        if (row) {
          setDetailProspect(row);
          setDetailOpen(true);
          setHighlightedId(openId);
          setTimeout(() => setHighlightedId((v) => (v === openId ? null : v)), 10_000);
          setFilterStatus("all");
          setFilterRate("all");
          setFilterOwner("all");
          setSearch("");
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="prospect-row-${openId}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 400);
        } else {
          toast.error("Prospek tidak ditemukan");
        }
      } catch {
        toast.error("Gagal memuat prospek");
      }
      // consume the param
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    })();
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    let arr = rows.filter((r) => {
      if (scope === "mine" && r.owner_id !== user?.user_id) return false;
      if (filterStatus !== "all" && (r.status || "active") !== filterStatus) return false;
      if (filterRate !== "all" && r.rate !== Number(filterRate)) return false;
      if (filterOwner !== "all" && r.owner_id !== filterOwner) return false;
      if (filterLeadSource !== "all" && (r.lead_source || "") !== filterLeadSource) return false;
      if (filterBulan !== "all" && rowMonthKey(r) !== filterBulan) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.customer.toLowerCase().includes(s) || r.service.toLowerCase().includes(s) || (r.pic_name || "").toLowerCase().includes(s))) return false;
      }
      return true;
    });
    if (sortBy && sortDir) {
      const dir = sortDir === "asc" ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        const va = sortAccessor(a, sortBy);
        const vb = sortAccessor(b, sortBy);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
        return String(va).localeCompare(String(vb), "id") * dir;
      });
    }
    return arr;
  }, [rows, filterRate, filterOwner, filterLeadSource, filterStatus, filterBulan, search, sortBy, sortDir, scope, user]);

  // ---- Pagination ----
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("sp_prospects_page_size");
    return ["25", "50", "100", "all"].includes(saved) ? saved : "25";
  });
  const [page, setPage] = useState(1);
  useEffect(() => { localStorage.setItem("sp_prospects_page_size", pageSize); }, [pageSize]);
  // Reset to page 1 whenever filter/scope/sort/size changes
  useEffect(() => { setPage(1); }, [filterRate, filterOwner, filterLeadSource, filterStatus, filterBulan, search, sortBy, sortDir, scope, pageSize]);
  const { paged, totalPages, curPage, startIdx, endIdx } = paginate(filtered, pageSize, page);

  const monthOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { const mk = rowMonthKey(r); if (mk) set.add(mk); });
    return Array.from(set).sort().reverse();
  }, [rows]);

  // Per-sales count for the current month scope (or all months when filterBulan="all")
  const monthlySummary = useMemo(() => {
    const scope = rows.filter((r) => filterBulan === "all" || rowMonthKey(r) === filterBulan);
    const bySales = new Map();
    scope.forEach((r) => {
      const key = r.owner_id || "unknown";
      if (!bySales.has(key)) bySales.set(key, { user_id: key, name: r.owner_nickname || r.owner_name || "?", picture: r.owner_picture || "", count: 0, deal: 0 });
      const b = bySales.get(key);
      b.count += 1;
      if ((r.status || "active") === "won") b.deal += 1;
    });
    return Array.from(bySales.values()).sort((a, b) => b.count - a.count);
  }, [rows, filterBulan]);

  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortBy(null); setSortDir(null);
  };

  const scopedRows = useMemo(() => {
    if (scope !== "mine" || !user) return rows;
    return rows.filter((r) => r.owner_id === user.user_id);
  }, [rows, scope, user]);

  const counts = useMemo(() => {
    const c = { active: 0, won: 0, lost: 0 };
    scopedRows.forEach((r) => { c[r.status || "active"]++; });
    return c;
  }, [scopedRows]);

  const canEdit = (row) => user?.role === "owner" || row.owner_id === user?.user_id;

  const markLost = async (row) => {
    if (!window.confirm(`Tandai "${row.customer}" sebagai Lost?`)) return;
    try {
      await api.post(`/prospects/${row.id}/lost`, {});
      toast.success("Ditandai Lost");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const reopen = async (row) => {
    try {
      await api.post(`/prospects/${row.id}/reopen`);
      toast.success("Prospek dibuka kembali");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/prospects/${deleteId}`);
      toast.success("Prospek dihapus");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal hapus");
    }
  };

  const exportXLSX = async () => {
    try {
      const r = await api.get("/prospects/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
      a.download = `prospek_${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal ekspor Excel");
    }
  };

  // Breakdown by status for the header subtitle — always computed from `filtered` rows
  const bd = filtered.reduce(
    (a, r) => {
      const st = r.status || "active";
      if (st === "active") { a.aktif.count += 1; a.aktif.value += r.prospect_value || 0; }
      else if (st === "won") { a.deal.count += 1; a.deal.value += r.realization || 0; }
      else if (st === "lost") { a.lost.count += 1; a.lost.value += r.prospect_value || 0; }
      return a;
    },
    { aktif: { count: 0, value: 0 }, deal: { count: 0, value: 0 }, lost: { count: 0, value: 0 } }
  );

  const renderSubtitle = () => {
    if (filterStatus === "active") {
      return <><span className="font-semibold text-[#090808]">{bd.aktif.count}</span> prospek · <span className="text-[#090808] font-semibold">{fmtIDR(bd.aktif.value)}</span> pipeline</>;
    }
    if (filterStatus === "won") {
      return <><span className="font-semibold text-[#57D28A]">{bd.deal.count}</span> deal · <span className="text-[#57D28A] font-semibold">{fmtIDR(bd.deal.value)}</span> realisasi</>;
    }
    if (filterStatus === "lost") {
      return <><span className="font-semibold text-[#626061]">{bd.lost.count}</span> lost · <span className="text-[#626061] font-semibold">{fmtIDR(bd.lost.value)}</span> nilai</>;
    }
    // "all"
    return (
      <>
        <span className="font-semibold text-[#090808]">{bd.aktif.count}</span> prospek · <span className="text-[#090808] font-semibold">{fmtIDR(bd.aktif.value)}</span>
        <span className="text-[#DCE0E7] mx-2">|</span>
        <span className="font-semibold text-[#57D28A]">{bd.deal.count}</span> deal · <span className="text-[#57D28A] font-semibold">{fmtIDR(bd.deal.value)}</span>
        <span className="text-[#DCE0E7] mx-2">|</span>
        <span className="font-semibold text-[#626061]">{bd.lost.count}</span> lost · <span className="text-[#626061] font-semibold">{fmtIDR(bd.lost.value)}</span>
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Pipeline</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Prospek</h1>
          <p className="text-sm text-[#A0A3A8] mt-1" data-testid="prospect-header-subtitle">
            {renderSubtitle()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 md:justify-end">
          <div className="inline-flex bg-white card-soft rounded-full p-1 gap-1">
            <button
              data-testid="prospect-view-table"
              onClick={() => setCompact(true)}
              className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                compact ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
              }`}
              title="Tampilan Tabel"
            >
              <Rows3 className="w-3.5 h-3.5" /> Tabel
            </button>
            <button
              data-testid="prospect-view-card"
              onClick={() => setCompact(false)}
              className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                !compact ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
              }`}
              title="Tampilan Kartu"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kartu
            </button>
          </div>
          <Button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="add-prospect-btn"
            className="gap-2 rounded-full h-11 bg-[#090808] hover:bg-[#000000] px-5 shadow-[0_6px_16px_rgba(9,8,8,0.25)]"
          >
            <Plus className="w-4 h-4" /> Tambah Prospek
          </Button>
        </div>
      </div>

      {/* Scope + Status tabs */}
      <div className="flex flex-wrap items-center gap-3">
        {!isOwner && (
          <>
            <div className="inline-flex items-center rounded-full bg-white card-soft p-1 shrink-0" data-testid="scope-toggle">
              <button
                onClick={() => setScope("mine")}
                data-testid="scope-mine"
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                  scope === "mine" ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Prospek Saya
              </button>
              <button
                onClick={() => setScope("all")}
                data-testid="scope-all"
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                  scope === "all" ? "bg-[#090808] text-white" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Semua Tim
              </button>
            </div>
            <div className="w-px h-6 bg-[#DCE0E7] hidden md:block" />
          </>
        )}
        <div className="flex flex-wrap gap-2">
        {[
          { key: "active", label: "Aktif", count: counts.active, color: "bg-[#090808] text-white" },
          { key: "won", label: "Deal", count: counts.won, color: "bg-[#57D28A] text-white" },
          { key: "lost", label: "Lost", count: counts.lost, color: "bg-[#A0A3A8] text-white" },
          { key: "all", label: "Semua", count: scopedRows.length, color: "bg-[#090808] text-white" },
        ].map((t) => {
          const active = filterStatus === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFilterStatus(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-all ${
                active ? `${t.color} shadow-[0_4px_12px_rgba(0,0,0,0.10)]` : "bg-white text-[#626061] hover:bg-[#F4F5F7] card-soft"
              }`}
            >
              {t.label}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                active ? "bg-white/25 text-white" : "bg-[#F4F5F7] text-[#626061]"
              }`}>{t.count}</span>
            </button>
          );
        })}
        </div>
        <div className="flex flex-wrap items-center gap-2.5 md:ml-auto">
          <Button variant="outline" onClick={exportXLSX} data-testid="export-xlsx-btn" className="gap-2 rounded-full h-10 border-[#DCE0E7] bg-white hover:bg-[#F4F5F7] px-5">
            <Download className="w-4 h-4 text-[#090808]" /> Download
          </Button>
          {(user?.role === "owner" || user?.role === "sales_director") && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn" className="gap-2 rounded-full h-10 border-[#DCE0E7] bg-white hover:bg-[#F4F5F7] px-5">
              <Upload className="w-4 h-4 text-[#090808]" /> Import
            </Button>
          )}
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
          <Input
            data-testid="search-input"
            placeholder="Cari customer, service, PIC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 rounded-full bg-white border-transparent card-soft focus-visible:ring-[#090808]/40"
          />
        </div>
        <Select value={filterRate} onValueChange={setFilterRate}>
          <SelectTrigger data-testid="filter-rate" className="w-44 h-12 rounded-full bg-white border-transparent card-soft px-5"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Stage</SelectItem>
            {[1, 2, 3].map((r) => (
              <SelectItem key={r} value={String(r)}>{r} · {RATE_META[r].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterOwner} onValueChange={setFilterOwner}>
          <SelectTrigger data-testid="filter-owner" className="w-52 h-12 rounded-full bg-white border-transparent card-soft px-5"><SelectValue placeholder="Tim" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tim</SelectItem>
            {users.filter((u) => u.role !== "owner" && u.role !== "finance").map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                  <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterBulan} onValueChange={setFilterBulan}>
          <SelectTrigger data-testid="filter-bulan" className="w-44 h-12 rounded-full bg-white border-transparent card-soft px-5"><SelectValue placeholder="Bulan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {monthOptions.map((mk) => (
              <SelectItem key={mk} value={mk}>{fmtMonthKey(mk)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterLeadSource} onValueChange={setFilterLeadSource}>
          <SelectTrigger data-testid="filter-lead-source" className="w-48 h-12 rounded-full bg-white border-transparent card-soft px-5"><SelectValue placeholder="Sumber" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sumber</SelectItem>
            {Object.entries(LEAD_SOURCE_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canDelete && (
          <div className="relative ml-auto" ref={kebabRef}>
            <button
              type="button"
              onClick={() => setKebabOpen((v) => !v)}
              data-testid="prospect-kebab-btn"
              title="Aksi lain"
              aria-label="Aksi lain"
              aria-expanded={kebabOpen}
              className={`w-12 h-12 rounded-full bg-white card-soft flex items-center justify-center transition-colors ${
                selectionMode ? "text-[#090808]" : "text-[#626061] hover:text-[#090808]"
              }`}
            >
              <MoreVertical className="w-4 h-4" strokeWidth={2.2} />
            </button>
            {kebabOpen && (
              <div
                data-testid="prospect-kebab-menu"
                className="absolute right-0 top-14 z-30 w-60 rounded-2xl bg-white card-soft border border-[#DCE0E7] py-1.5 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => { setKebabOpen(false); if (selectionMode) exitSelectionMode(); else setSelectionMode(true); }}
                  data-testid="prospect-toggle-selection-mode"
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#090808] hover:bg-[#F5F3FF] transition-colors"
                >
                  <CheckSquare className="w-4 h-4 text-[#090808]" />
                  <span className="font-medium">{selectionMode ? "Keluar mode pilih" : "Aktifkan mode pilih"}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk delete bar (mirrors Klien) */}
      {selectionMode && selectedIds.size > 0 && canDelete && (
        <div
          className="rounded-2xl bg-white card-soft px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="prospect-bulk-bar"
        >
          <div className="inline-flex items-center gap-2 text-sm">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#090808] text-white font-bold text-xs">
              {selectedIds.size}
            </span>
            <span>prospek dipilih</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setSelectedIds(new Set())}
              data-testid="prospect-bulk-clear"
              className="h-9 rounded-full"
            >Bersihkan</Button>
            <Button
              variant="outline"
              onClick={exitSelectionMode}
              data-testid="prospect-bulk-cancel"
              className="h-9 rounded-full"
            >Batal</Button>
            <Button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="prospect-bulk-delete-btn"
              className="h-9 rounded-full bg-[#CE6968] hover:bg-[#A6494A] gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> {bulkBusy ? "Menghapus..." : `Hapus ${selectedIds.size} Prospek`}
            </Button>
          </div>
        </div>
      )}
      {selectionMode && selectedIds.size === 0 && canDelete && (
        <div className="rounded-2xl bg-[#F4F5F7] px-4 py-2.5 text-xs text-[#626061] flex items-center justify-between">
          <span>Mode pilih aktif — klik baris untuk memilih prospek yang ingin dihapus</span>
          <button onClick={exitSelectionMode} data-testid="prospect-selection-exit" className="text-[#090808] font-semibold hover:underline">Batal</button>
        </div>
      )}

      {/* Monthly summary strip */}
      {monthlySummary.length > 0 && (
        <div className="rounded-2xl bg-white card-soft px-4 py-3" data-testid="monthly-summary">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">
              Ringkasan Prospek Baru {filterBulan === "all" ? "· Semua Bulan" : `· ${fmtMonthKey(filterBulan)}`}
            </div>
            <div className="text-xs text-[#A0A3A8]">
              Total <span className="font-semibold text-[#090808]">{monthlySummary.reduce((s, r) => s + r.count, 0)}</span> prospek
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {monthlySummary.map((s) => (
              <div
                key={s.user_id}
                data-testid={`month-summary-${s.user_id}`}
                className="shrink-0 inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-[#F4F5F7]"
              >
                <Avatar name={s.name} picture={s.picture} size="xs" />
                <div className="text-xs">
                  <span className="font-semibold text-[#090808]">{s.name}</span>
                  <span className="text-[#A0A3A8]"> · </span>
                  <span className="font-bold tabular-nums text-[#090808]">{s.count}</span>
                  {s.deal > 0 && (
                    <span className="text-[10px] font-semibold tabular-nums text-[#57D28A] ml-1.5">({s.deal} deal)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Column headers (comfy mode) */}
      {!compact && (
        <div className="hidden md:grid grid-cols-[80px_120px_1fr_1fr_140px_140px_180px_100px_90px_100px] gap-4 px-6 text-[11px] uppercase tracking-widest text-[#626061] font-bold">
          <div><SortHeader label="Stage" sortKey="rate" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div><SortHeader label="Sales" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div><SortHeader label="Customer" sortKey="customer" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div><SortHeader label="Service" sortKey="service" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div className="text-right"><SortHeader label="Pipeline" sortKey="pipeline" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></div>
          <div className="text-right"><SortHeader label="Realisasi" sortKey="realisasi" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></div>
          <div><SortHeader label="PIC" sortKey="pic" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div><SortHeader label="Follow-up" sortKey="followup" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div><SortHeader label="Bulan" sortKey="bulan" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></div>
          <div></div>
        </div>
      )}

      {/* COMPACT (table) view */}
      {compact ? (
        <div className="bg-white border-0 rounded-2xl card-soft overflow-hidden" data-testid="prospect-table">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-[#A0A3A8]">
              Belum ada prospek. Klik <span className="font-medium text-[#090808]">Tambah Prospek</span> untuk mulai.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#626061] font-bold">
                  <tr>
                    {selectionMode && (
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          data-testid="prospect-select-all"
                          checked={paged.length > 0 && paged.every((r) => selectedIds.has(r.id))}
                          ref={(el) => { if (el) el.indeterminate = paged.some((r) => selectedIds.has(r.id)) && !paged.every((r) => selectedIds.has(r.id)); }}
                          onChange={() => {
                            const allOn = paged.every((r) => selectedIds.has(r.id));
                            setSelectedIds((s) => { const n = new Set(s); paged.forEach((r) => allOn ? n.delete(r.id) : n.add(r.id)); return n; });
                          }}
                          className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="text-left pl-6 pr-2 py-3 w-20"><SortHeader label="Stage" sortKey="rate" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="Sales" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="Customer" sortKey="customer" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="Service" sortKey="service" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-right px-2 py-3"><SortHeader label="Pipeline" sortKey="pipeline" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                    <th className="text-right px-2 py-3"><SortHeader label="Realisasi" sortKey="realisasi" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="PIC" sortKey="pic" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="Follow-up" sortKey="followup" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="text-left px-2 py-3"><SortHeader label="Bulan" sortKey="bulan" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                    <th className="px-3 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((r, i) => {
                    const m = RATE_META[r.rate];
                    const st = r.status || "active";
                    const isTerminal = st !== "active";
                    return (
                      <tr
                        key={r.id}
                        data-testid={`prospect-row-${r.id}`}
                        onClick={() => handleRowOpen(r)}
                        className={`border-t border-[#DCE0E7] hover:bg-[#F4F5F7] cursor-pointer transition-colors ${st === "lost" ? "opacity-70" : ""} ${highlightedId === r.id ? "bg-[#F4F5F7]/70" : ""} ${selectionMode && selectedIds.has(r.id) ? "bg-[#F4F5F7]/70" : ""}`}
                      >
                        {selectionMode && (
                          <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              data-testid={`prospect-select-${r.id}`}
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleId(r.id)}
                              className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="pl-6 pr-2 py-2.5">
                          {isTerminal ? (
                            <div className={`inline-flex items-center h-6 px-2 rounded-full text-[10px] font-bold text-white ${st === "won" ? "bg-[#57D28A]" : "bg-[#A0A3A8]"}`}>
                              {STATUS_META[st].label.toUpperCase()}
                            </div>
                          ) : (
                            <div className={`w-7 h-7 rounded-full ${m.pill} text-white font-bold flex items-center justify-center text-[11px]`}>
                              {m.letter}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar name={r.owner_name} picture={r.owner_picture} size="xs" />
                            <div className="text-[#090808] font-medium truncate text-xs">{r.owner_nickname || r.owner_name}</div>
                          </div>
                        </td>
                        <td className="px-2 py-2.5 font-semibold text-[#090808] max-w-[220px] truncate" title={r.customer}>{r.customer}</td>
                        <td className="px-2 py-2.5 text-[#626061] text-xs max-w-[220px] truncate" title={r.service}>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{r.service}</span>
                            {r.lead_source && LEAD_SOURCE_META[r.lead_source] && (
                              <span className={`inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest shrink-0 ${LEAD_SOURCE_META[r.lead_source].tint}`} title={`Sumber: ${LEAD_SOURCE_META[r.lead_source].label}${r.lead_source_detail ? ` — ${r.lead_source_detail}` : ""}`}>
                                {LEAD_SOURCE_META[r.lead_source].label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-right font-num text-xs text-[#090808] font-semibold tabular-nums whitespace-nowrap">{fmtIDR(r.prospect_value)}</td>
                        <td className="px-2 py-2.5 text-right font-num text-xs text-[#57D28A] font-semibold tabular-nums whitespace-nowrap">{st === "won" ? fmtIDR(r.realization) : "-"}</td>
                        <td className="px-2 py-2.5 text-xs text-[#090808] max-w-[160px] truncate" title={r.pic_name || ""}>{r.pic_name || "-"}</td>
                        <td className="px-2 py-2.5 text-xs text-[#626061] tabular-nums whitespace-nowrap">{fmtDate(st === "won" ? r.deal_date : r.last_follow_up)}</td>
                        <td className="px-2 py-2.5 text-xs text-[#626061] tabular-nums whitespace-nowrap">{fmtMonthKey(rowMonthKey(r))}</td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end items-center gap-0.5">
                            <AttachmentPopover prospect={r} canEdit={canEdit(r)} onChanged={load} />
                            <RowMenu
                              row={r}
                              canEdit={canEdit(r)}
                              onEdit={() => { setDetailProspect(r); setDetailOpen(true); }}
                              onDeal={() => setDealFor(r)}
                              onLost={() => markLost(r)}
                              onReopen={() => reopen(r)}
                              onDelete={() => setDeleteId(r.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
      /* Comfy (card) view — informative grid */
      <div>
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl py-16 text-center text-sm text-[#A0A3A8] card-soft">
            Belum ada prospek. Klik <span className="font-medium text-[#090808]">Tambah Prospek</span> untuk mulai.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="prospect-card-grid">
            {paged.map((r) => (
              <ProspectCard
                key={r.id}
                row={r}
                selectionMode={selectionMode}
                selected={selectedIds.has(r.id)}
                onToggleSelect={() => toggleId(r.id)}
                onOpen={() => handleRowOpen(r)}
                highlighted={highlightedId === r.id}
                canEdit={canEdit(r)}
                onReload={load}
                onEdit={() => { setDetailProspect(r); setDetailOpen(true); }}
                onDeal={() => setDealFor(r)}
                onLost={() => markLost(r)}
                onReopen={() => reopen(r)}
                onDelete={() => setDeleteId(r.id)}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {filtered.length > 0 && (
        <Pagination
          total={filtered.length}
          page={curPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
          startIdx={startIdx}
          endIdx={endIdx}
          label="prospek"
          testidPrefix="prospect-pagination"
        />
      )}

      <ProspectFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSaved={load}
        onRequestDeal={(p) => setDealFor(p)}
      />
      <DealModal prospect={dealFor} onOpenChange={(o) => !o && setDealFor(null)} onDone={load} />
      <ImportProspectModal open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <ProspectDetailModal
        open={detailOpen}
        prospect={detailProspect}
        users={users}
        onClose={() => { setDetailOpen(false); setDetailProspect(null); }}
        onChanged={() => { load(); if (detailProspect) api.get(`/prospects/${detailProspect.id}`).then((r) => setDetailProspect(r.data)).catch(() => {}); }}
        onDeal={() => { setDetailOpen(false); setDealFor(detailProspect); }}
        onLost={() => { setDetailOpen(false); markLost(detailProspect); }}
        onReopen={() => { setDetailOpen(false); reopen(detailProspect); }}
        onDelete={() => { setDetailOpen(false); setDeleteId(detailProspect.id); }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus prospek ini?</AlertDialogTitle>
            <AlertDialogDescription>Aksi ini tidak bisa dibatalkan. Semua history akan ikut terhapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const IconBtn = ({ children, danger, small, ...props }) => (
  <button
    {...props}
    className={`${small ? "w-7 h-7" : "w-9 h-9"} inline-flex items-center justify-center rounded-full hover:bg-[#F4F5F7] text-[#626061] ${danger ? "hover:text-red-500" : "hover:text-[#090808]"} transition-colors`}
  >{children}</button>
);

// Glassy tint per rate (matches Kanban board style) — returns inline style for translucency + backdrop-blur
function rateGlass(rate, status) {
  if (status === "won")  return { bg: "linear-gradient(160deg, rgba(180,240,205,0.42) 0%, rgba(200,246,220,0.28) 100%)", border: "rgba(120,200,150,0.35)" };
  if (status === "lost") return { bg: "linear-gradient(160deg, rgba(220,224,231,0.42) 0%, rgba(232,234,239,0.26) 100%)", border: "rgba(160,163,168,0.30)" };
  if (rate === 1) return { bg: "linear-gradient(160deg, rgba(255,193,203,0.42) 0%, rgba(255,213,220,0.28) 100%)", border: "rgba(255,140,155,0.35)" };
  if (rate === 2) return { bg: "linear-gradient(160deg, rgba(255,215,170,0.42) 0%, rgba(255,225,190,0.28) 100%)", border: "rgba(255,180,110,0.35)" };
  return { bg: "linear-gradient(160deg, rgba(190,210,240,0.42) 0%, rgba(210,222,242,0.28) 100%)", border: "rgba(150,175,220,0.35)" };
}

function ProspectCard({ row, selectionMode, selected, onToggleSelect, onOpen, highlighted, canEdit, onReload, onEdit, onDeal, onLost, onReopen, onDelete }) {
  const r = row;
  const m = RATE_META[r.rate];
  const st = r.status || "active";
  const isTerminal = st !== "active";
  const tint = rateGlass(r.rate, st);

  // Overdue check: last_follow_up > 7 days ago and still active
  let overdue = false;
  if (!isTerminal && r.last_follow_up) {
    const diff = (Date.now() - new Date(r.last_follow_up).getTime()) / (1000 * 60 * 60 * 24);
    overdue = diff > 7;
  }

  return (
    <div
      data-testid={`prospect-row-${r.id}`}
      onClick={onOpen}
      className={`group relative rounded-2xl cursor-pointer hover:shadow-[0_10px_28px_rgba(24,27,51,0.14),0_24px_60px_rgba(24,27,51,0.10)] transition-shadow ${st === "lost" ? "opacity-70" : ""} ${highlighted ? "ring-2 ring-[#090808]/50 shadow-[0_0_0_4px_rgba(9,8,8,0.08)]" : ""} ${selectionMode && selected ? "ring-2 ring-[#090808]/60" : ""}`}
      style={{
        background: tint.bg,
        backdropFilter: "blur(24px) saturate(160%)",
        WebkitBackdropFilter: "blur(24px) saturate(160%)",
        border: `1px solid ${tint.border}`,
        boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
      }}
    >
      {selectionMode && (
        <div className="absolute left-3 top-3 z-10" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            data-testid={`prospect-select-${r.id}`}
            checked={selected}
            onChange={onToggleSelect}
            className="w-5 h-5 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer bg-white"
          />
        </div>
      )}

      {/* Header — Rate/Status pill + customer + value + menu */}
      <div className={`px-5 pt-5 pb-3 ${selectionMode ? "pl-12" : ""}`}>
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            {isTerminal ? (
              <div className={`inline-flex items-center gap-1 h-9 px-3 rounded-full font-bold text-xs text-white shadow-[0_4px_10px_rgba(0,0,0,0.10)] ${st === "won" ? "bg-[#57D28A]" : "bg-[#A0A3A8]"}`}>
                {st === "won" && <PartyPopper className="w-3.5 h-3.5" />}
                {STATUS_META[st].label.toUpperCase()}
              </div>
            ) : (
              <div className={`w-10 h-10 rounded-full ${m.pill} text-white font-bold flex items-center justify-center text-base shadow-[0_4px_10px_rgba(0,0,0,0.10)]`} title={m.label}>
                {m.letter}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-[#090808] leading-tight truncate">{r.customer}</div>
            <div className="text-xs text-[#626061] mt-0.5 flex items-center gap-1.5 min-w-0">
              <span className="truncate">{r.service || "-"}</span>
              {r.lead_source && LEAD_SOURCE_META[r.lead_source] && (
                <span className={`inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest shrink-0 ${LEAD_SOURCE_META[r.lead_source].tint}`} title={`Sumber: ${LEAD_SOURCE_META[r.lead_source].label}${r.lead_source_detail ? ` — ${r.lead_source_detail}` : ""}`}>
                  {LEAD_SOURCE_META[r.lead_source].label}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
            <AttachmentPopover prospect={r} canEdit={canEdit} onChanged={onReload} />
            <RowMenu
              row={r}
              canEdit={canEdit}
              onEdit={onEdit}
              onDeal={onDeal}
              onLost={onLost}
              onReopen={onReopen}
              onDelete={onDelete}
            />
          </div>
        </div>

        {/* Values row */}
        <div className="mt-3 flex items-baseline justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#626061] font-semibold">Pipeline</div>
            <div className="font-num text-base font-bold text-[#090808] tabular-nums leading-tight">{fmtIDR(r.prospect_value)}</div>
          </div>
          {st === "won" && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#4A6D53] font-semibold">Realisasi</div>
              <div className="font-num text-base font-bold text-[#57D28A] tabular-nums leading-tight">{fmtIDR(r.realization)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="mx-5 h-px" style={{ background: "rgba(255,255,255,0.55)" }} />

      {/* Body — Sales + PIC */}
      <div className="px-5 py-3 space-y-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar name={r.owner_name} picture={r.owner_picture} size="sm" />
          <div className="min-w-0">
            <div className="text-xs text-[#626061]">Sales</div>
            <div className="text-sm font-semibold text-[#090808] truncate">{r.owner_nickname || r.owner_name}</div>
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-[#626061]">PIC</div>
          <div className="text-sm font-semibold text-[#090808] truncate">{r.pic_name || "-"}</div>
          {r.pic_position && (
            <div className="text-xs text-[#626061] truncate">{r.pic_position}</div>
          )}
          {r.pic_contact && (
            <div className="mt-0.5"><ContactLink contact={r.pic_contact} /></div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="mx-5 h-px" style={{ background: "rgba(255,255,255,0.55)" }} />

      {/* Footer — Follow-up / Bulan */}
      <div className="px-5 py-3 flex items-center justify-between gap-2 text-xs">
        <div className={`inline-flex items-center gap-1.5 font-medium tabular-nums ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`}>
          {overdue && <span className="w-1.5 h-1.5 rounded-full bg-[#CE6968]" />}
          {st === "won" ? (
            <span className="text-[#4A6D53] font-semibold">Deal · {fmtDate(r.deal_date)}</span>
          ) : (
            <>Follow-up · {fmtDate(r.last_follow_up)}</>
          )}
        </div>
        <div className="text-[#626061] tabular-nums font-medium">{fmtMonthKey(rowMonthKey(r))}</div>
      </div>

      {/* Remark — only if present */}
      {r.remark && (
        <div className="mx-5 mb-4 rounded-xl px-3 py-2 text-xs text-[#626061] leading-relaxed" style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.55)" }}>
          <span className="font-bold text-[#090808]">Remark:</span> {r.remark}
        </div>
      )}
    </div>
  );
}

function ContactLink({ contact }) {
  const raw = String(contact || "").trim();
  if (!raw) return null;

  // Split on common separators: middle dot, comma, semicolon, pipe, slash, or whitespace runs between tokens
  const tokens = raw.split(/\s*[·,;|/\n]\s*|\s{2,}/).map((t) => t.trim()).filter(Boolean);

  const buildLink = (token, idx) => {
    const isEmail = /@/.test(token) && /\.[a-z]{2,}/i.test(token);
    let href = "#";
    let target;
    let rel;
    if (isEmail) {
      href = `mailto:${token}`;
    } else {
      let digits = token.replace(/\D/g, "");
      if (!digits) return <span key={idx} className="text-xs text-[#626061]">{token}</span>;
      if (digits.startsWith("0")) digits = "62" + digits.slice(1);
      href = `https://wa.me/${digits}`;
      target = "_blank";
      rel = "noopener noreferrer";
    }
    return (
      <a
        key={idx}
        href={href}
        target={target}
        rel={rel}
        onClick={(e) => e.stopPropagation()}
        data-testid="pic-contact-link"
        className="inline-block text-xs text-[#090808] font-semibold hover:underline"
      >
        {token}
      </a>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 max-w-full">
      {tokens.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 min-w-0">
          {buildLink(t, i)}
          {i < tokens.length - 1 && <span className="text-[#A0A3A8] text-xs">·</span>}
        </span>
      ))}
    </div>
  );
}

function sortAccessor(row, key) {
  switch (key) {
    case "rate": return row.rate;
    case "sales": return row.owner_nickname || row.owner_name || "";
    case "customer": return row.customer || "";
    case "service": return row.service || "";
    case "pipeline": return row.prospect_value || 0;
    case "realisasi": return row.realization || 0;
    case "pic": return row.pic_name || "";
    case "followup": return (row.status === "won" ? row.deal_date : row.last_follow_up) || "";
    case "bulan": return rowMonthKey(row) || "";
    default: return null;
  }
}

const MONTH_LBL = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
function fmtMonthKey(k) {
  if (!k || k.length < 7) return "-";
  const [y, m] = k.split("-");
  const idx = Number(m) - 1;
  return `${MONTH_LBL[idx] || m} '${y.slice(2)}`;
}

// Prefer explicit pipeline_month; fallback to created_at
function rowMonthKey(r) {
  return (r.pipeline_month || (r.created_at || "").slice(0, 7));
}

function SortGlyph({ dir }) {
  const upOp = dir === "desc" ? 0.25 : dir === "asc" ? 1 : 0.55;
  const downOp = dir === "asc" ? 0.25 : dir === "desc" ? 1 : 0.55;
  return (
    <svg width="8" height="11" viewBox="0 0 8 11" aria-hidden="true" className="shrink-0">
      <path d="M4 0.5L7.5 4.2H0.5L4 0.5Z" fill="currentColor" opacity={upOp} />
      <path d="M4 10.5L0.5 6.8H7.5L4 10.5Z" fill="currentColor" opacity={downOp} />
    </svg>
  );
}

function SortHeader({ label, sortKey, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === sortKey;
  const dir = active ? sortDir : null;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`sort-${sortKey}`}
      className={`inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#090808] transition-colors ${active ? "text-[#090808]" : ""}`}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {align === "right" && <SortGlyph dir={dir} />}
      <span>{label}</span>
      {align !== "right" && <SortGlyph dir={dir} />}
    </button>
  );
}

function RowMenu({ row, canEdit, onEdit, onDeal, onLost, onReopen, onDelete }) {
  const st = row.status || "active";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`row-menu-${row.id}`}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-[#F4F5F7] text-[#626061] hover:text-[#090808] transition-colors"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl w-52">
        {canEdit && (
          <DropdownMenuItem onClick={onEdit} data-testid={`menu-edit-${row.id}`}>
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </DropdownMenuItem>
        )}
        {canEdit && st === "active" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDeal} data-testid={`menu-deal-${row.id}`} className="text-[#57D28A] focus:text-[#4A6D53] focus:bg-emerald-50">
              <PartyPopper className="w-4 h-4 mr-2" /> Tandai Deal
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLost} data-testid={`menu-lost-${row.id}`} className="text-[#626061] focus:text-[#626061] focus:bg-slate-50">
              <XCircle className="w-4 h-4 mr-2" /> Tandai Lost
            </DropdownMenuItem>
          </>
        )}
        {canEdit && st !== "active" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onReopen} data-testid={`menu-reopen-${row.id}`}>
              <RotateCcw className="w-4 h-4 mr-2" /> Buka Kembali
            </DropdownMenuItem>
          </>
        )}
        {canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} data-testid={`menu-delete-${row.id}`} className="text-red-500 focus:text-red-600 focus:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" /> Hapus
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
